import {
    Box,
    Collapse,
    IconButton,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Typography,
} from '@mui/material';
import { EditIcon, MoreVertical, Plus, Repeat, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import SensitiveInfoTypography from '~/components/SensitiveInfoTypography';
import { RegistrationViewModel } from '~/domain/readmodels/RegistrationReadModel';

interface RegistrationMenuProps {
    isApproved?: boolean;
    isDisabled?: boolean;
    hasSharedFrameOfReference?: boolean;
    availableRegistrations?: ReadonlyArray<RegistrationViewModel>;
    activeRegistrationSopUid?: string;
    onRename: () => void;
    onCreateNew: () => void;
    onDelete: () => void;
    onChangeRegistration?: (sopInstanceUid: string) => void;
}

export default function RegistrationMenu({
    isApproved = false,
    isDisabled = false,
    hasSharedFrameOfReference = false,
    availableRegistrations = [],
    activeRegistrationSopUid,
    onRename,
    onCreateNew,
    onDelete,
    onChangeRegistration,
}: RegistrationMenuProps) {
    const intl = useIntl();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [isChangeExpanded, setIsChangeExpanded] = useState(false);
    const isOpen = Boolean(anchorEl);

    const canChangeRegistration =
        availableRegistrations.length > 1 && onChangeRegistration !== undefined;

    const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
        setIsChangeExpanded(false);
    };

    const handleRename = () => {
        handleClose();
        onRename();
    };

    const handleCreateNew = () => {
        handleClose();
        onCreateNew();
    };

    const handleDelete = () => {
        handleClose();
        onDelete();
    };

    const handleSelectRegistration = (sopInstanceUid: string) => {
        if (
            onChangeRegistration &&
            sopInstanceUid !== activeRegistrationSopUid
        ) {
            onChangeRegistration(sopInstanceUid);
        }
        handleClose();
    };

    return (
        <>
            <IconButton
                size="small"
                onClick={handleOpen}
                sx={{ p: 0.25 }}
                data-testid="registration-menu-button"
                aria-label={intl.formatMessage({ id: 'moreActions' })}
            >
                <MoreVertical size={12} />
            </IconButton>
            <Menu
                anchorEl={anchorEl}
                open={isOpen}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
            >
                {canChangeRegistration && (
                    <>
                        <MenuItem
                            onClick={() =>
                                setIsChangeExpanded(!isChangeExpanded)
                            }
                            selected={isChangeExpanded}
                            data-testid="change-registration-button"
                        >
                            <ListItemIcon>
                                <Repeat size={12} />
                            </ListItemIcon>
                            <ListItemText>
                                <FormattedMessage id="changeRegistration" />
                            </ListItemText>
                        </MenuItem>
                        <Collapse in={isChangeExpanded} unmountOnExit>
                            <Box
                                display="flex"
                                flexDirection="column"
                                maxHeight={300}
                                overflow="auto"
                            >
                                {availableRegistrations.map((reg) => {
                                    const isActive =
                                        reg.sopInstanceUid ===
                                        activeRegistrationSopUid;
                                    return (
                                        <ListItemButton
                                            key={reg.sopInstanceUid}
                                            selected={isActive}
                                            onClick={() =>
                                                handleSelectRegistration(
                                                    reg.sopInstanceUid
                                                )
                                            }
                                            sx={{ pl: 4.5, py: 0.5 }}
                                        >
                                            <Typography
                                                fontSize={12}
                                                color={
                                                    isActive
                                                        ? 'text.primary'
                                                        : 'text.secondary'
                                                }
                                                noWrap
                                            >
                                                <SensitiveInfoTypography>
                                                    {reg.contentLabel ||
                                                        reg.sopInstanceUid.slice(
                                                            0,
                                                            8
                                                        )}
                                                </SensitiveInfoTypography>
                                            </Typography>
                                        </ListItemButton>
                                    );
                                })}
                            </Box>
                        </Collapse>
                    </>
                )}
                <MenuItem
                    onClick={handleCreateNew}
                    disabled={hasSharedFrameOfReference || isDisabled}
                >
                    <ListItemIcon>
                        <Plus size={12} />
                    </ListItemIcon>
                    <ListItemText>
                        <FormattedMessage id="registrationNew" />
                    </ListItemText>
                </MenuItem>
                <MenuItem
                    onClick={handleRename}
                    disabled={isApproved || isDisabled}
                >
                    <ListItemIcon>
                        <EditIcon size={12} />
                    </ListItemIcon>
                    <ListItemText>
                        <FormattedMessage id="rename" />
                    </ListItemText>
                </MenuItem>

                <MenuItem
                    onClick={handleDelete}
                    disabled={isApproved || isDisabled}
                >
                    <ListItemIcon>
                        <Trash2 size={11} />
                    </ListItemIcon>
                    <ListItemText>
                        <FormattedMessage id="deleteRegistration" />
                    </ListItemText>
                </MenuItem>
            </Menu>
        </>
    );
}
